// Login.jsx
import React, { useState } from 'react';
import { loginUser, forgotPassword, resetPassword } from '../api';
import {
  CheckCircleIcon,
  ShoppingBagIcon,
  ArrowPathIcon,
  EnvelopeIcon,
  ArrowLeftIcon,
  KeyIcon,
  LockClosedIcon,
  EyeIcon,
  EyeSlashIcon,
} from '@heroicons/react/24/outline';

// screen: 'login' | 'forgot' | 'reset' | 'forgot-success'
export default function Login({ onClose, onSwitchToRegister, onLoginSuccess }) {
  const [screen, setScreen]   = useState('login');
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [loading, setLoading]   = useState(false);
  const [errors, setErrors]     = useState({});
  const [welcomeUser, setWelcomeUser] = useState(null);
  const [showPassword, setShowPassword] = useState(false);

  // Forgot password state
  const [fpEmail, setFpEmail]       = useState('');
  const [fpLoading, setFpLoading]   = useState(false);
  const [fpError, setFpError]       = useState('');
  const [fpToken, setFpToken]       = useState(''); // dev only

  // Reset password state
  const [rpToken, setRpToken]         = useState('');
  const [rpPassword, setRpPassword]   = useState('');
  const [rpConfirm, setRpConfirm]     = useState('');
  const [rpLoading, setRpLoading]     = useState(false);
  const [rpError, setRpError]         = useState('');
  const [rpSuccess, setRpSuccess]     = useState(false);

  // ── Login handlers ────────────────────────────────────────
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Email is invalid';
    if (!formData.password) newErrors.password = 'Password is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setLoading(true);
    try {
      console.log('🔐 Attempting login with:', { email: formData.email, password: '***' });
      const data = await loginUser({ email: formData.email, password: formData.password });
      console.log('📡 Login API response:', data);
      
      if (data.success) {
        console.log('✅ Login successful, storing user data:', data.user);
        localStorage.setItem('token', data.token);
        localStorage.setItem('currentUser', JSON.stringify(data.user));
        if (data.user?.location) {
          try {
            const loc = typeof data.user.location === 'string' ? JSON.parse(data.user.location) : data.user.location;
            localStorage.setItem('userLocation', JSON.stringify(loc));
            window.dispatchEvent(new Event('location-updated'));
          } catch {}
        }
        window.dispatchEvent(new Event('auth-changed'));
        if (onLoginSuccess) onLoginSuccess(data.user);
        setWelcomeUser(data.user);
      } else {
        console.error('❌ Login failed:', data.message);
        setErrors({ general: data.message || 'Invalid credentials. Please try again.' });
      }
    } catch (error) {
      console.error('🚨 Login error:', error);
      setErrors({ general: 'Unable to connect to the server. Please check if the server is running.' });
    } finally {
      setLoading(false);
    }
  };

  // ── Forgot password handlers ──────────────────────────────
  const handleForgotSubmit = async (e) => {
    e.preventDefault();
    if (!fpEmail.trim() || !/\S+@\S+\.\S+/.test(fpEmail)) {
      setFpError('Please enter a valid email address.');
      return;
    }
    setFpLoading(true);
    setFpError('');
    try {
      const data = await forgotPassword({ email: fpEmail });
      if (data.success) {
        setFpToken(data.dev_token || ''); // dev only — remove in production
        setScreen('forgot-success');
      } else {
        setFpError(data.message || 'Something went wrong. Please try again.');
      }
    } catch {
      setFpError('Unable to connect to server.');
    } finally {
      setFpLoading(false);
    }
  };

  // ── Reset password handlers ───────────────────────────────
  const handleResetSubmit = async (e) => {
    e.preventDefault();
    if (!rpToken.trim()) { setRpError('Please enter the reset token.'); return; }
    if (rpPassword.length < 8) { setRpError('Password must be at least 8 characters.'); return; }
    if (rpPassword !== rpConfirm) { setRpError('Passwords do not match.'); return; }
    setRpLoading(true);
    setRpError('');
    try {
      const data = await resetPassword({ token: rpToken, new_password: rpPassword });
      if (data.success) {
        setRpSuccess(true);
      } else {
        setRpError(data.message || 'Reset failed. Token may be expired.');
      }
    } catch {
      setRpError('Unable to connect to server.');
    } finally {
      setRpLoading(false);
    }
  };

  const inputStyle = (hasError) => ({
    width: '100%', padding: '0.72rem 0.875rem',
    border: `1px solid ${hasError ? '#ef4444' : '#d1d5db'}`,
    borderRadius: '8px', fontSize: '0.875rem',
    boxSizing: 'border-box', outline: 'none', transition: 'border-color 0.2s',
  });

  const baseStyle = {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 1000, padding: '16px'
  };

  const cardStyle = {
    background: 'white', borderRadius: '16px',
    maxWidth: '420px', width: '92%',
    padding: '2rem 1.75rem', position: 'relative',
    boxShadow: '0 12px 40px rgba(0,0,0,0.18)'
  };

  const globalStyle = `
    @keyframes fadeInUp { from { opacity:0; transform:translateY(16px); } to { opacity:1; transform:translateY(0); } }
    @keyframes spin { to { transform: rotate(360deg); } }
    
    @media (max-width: 768px) {
      .login-modal-overlay {
        padding: 12px !important;
      }
      .login-modal-card {
        width: 95% !important;
        max-width: 380px !important;
        padding: 1.5rem 1.25rem !important;
        border-radius: 12px !important;
      }
      .login-title {
        font-size: 1.1rem !important;
      }
      .login-subtitle {
        font-size: 0.8rem !important;
      }
      .login-input {
        padding: 0.65rem 0.75rem !important;
        font-size: 0.85rem !important;
      }
      .login-label {
        font-size: 0.8rem !important;
        margin-bottom: 0.3rem !important;
      }
      .login-btn {
        padding: 0.65rem !important;
        font-size: 0.85rem !important;
      }
      .login-close-btn {
        width: 28px !important;
        height: 28px !important;
        font-size: 1.2rem !important;
      }
    }
    
    @media (max-width: 480px) {
      .login-modal-card {
        width: 98% !important;
        max-width: 320px !important;
        padding: 1.25rem 1rem !important;
        margin: 0 !important;
      }
      .login-title {
        font-size: 1rem !important;
      }
      .login-subtitle {
        font-size: 0.75rem !important;
      }
      .login-input {
        padding: 0.6rem 0.7rem !important;
        font-size: 0.8rem !important;
      }
      .login-label {
        font-size: 0.75rem !important;
      }
      .login-btn {
        padding: 0.6rem !important;
        font-size: 0.8rem !important;
      }
      .forgot-link {
        font-size: 0.7rem !important;
      }
      .dev-token-code {
        font-size: 0.65rem !important;
        padding: 3px 5px !important;
      }
    }
  `;

  // ── Welcome popup ─────────────────────────────────────────
  if (welcomeUser) {
    const firstName = (welcomeUser.full_name || welcomeUser.name || '').split(' ')[0];
    return (
      <div style={baseStyle}>
        <div style={{ ...cardStyle, textAlign: 'center', animation: 'fadeInUp 0.3s ease' }}>
          <style>{globalStyle}</style>
          <div style={{ width: 56, height: 56, background: '#dcfce7', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1rem' }}>
            <CheckCircleIcon style={{ width: 28, height: 28, color: '#16a34a' }} />
          </div>
          <h2 style={{ margin: '0 0 0.3rem', fontSize: '1.2rem', fontWeight: 700, color: '#111827' }}>Welcome back, {firstName}!</h2>
          <p style={{ margin: '0 0 1.5rem', fontSize: '0.85rem', color: '#6b7280' }}>You're signed in successfully.</p>
          <button onClick={onClose} style={{ width: '100%', padding: '0.65rem', background: 'linear-gradient(135deg,#16a34a,#15803d)', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 600, fontSize: '0.9rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}
            onMouseEnter={e => e.currentTarget.style.opacity = '0.9'} onMouseLeave={e => e.currentTarget.style.opacity = '1'}>
            <ShoppingBagIcon style={{ width: 16, height: 16 }} /> Continue Shopping
          </button>
        </div>
      </div>
    );
  }

  // ── Forgot password — email input ─────────────────────────
  if (screen === 'forgot') {
    return (
      <div style={baseStyle} onClick={onClose}>
        <div style={cardStyle} onClick={e => e.stopPropagation()}>
          <style>{globalStyle}</style>
          <button onClick={onClose} style={{ position: 'absolute', top: '1rem', right: '1rem', background: 'none', border: 'none', fontSize: '1.4rem', cursor: 'pointer', color: '#9ca3af', width: 32, height: 32, borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            onMouseEnter={e => e.currentTarget.style.background = '#f3f4f6'} onMouseLeave={e => e.currentTarget.style.background = 'none'}>×</button>

          {/* Icon */}
          <div style={{ width: 48, height: 48, background: '#fef3c7', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '1rem' }}>
            <KeyIcon style={{ width: 24, height: 24, color: '#d97706' }} />
          </div>

          {/* <h2 style={{ margin: '0 0 0.25rem', fontSize: '1.2rem', fontWeight: 700, color: '#111827' }}>Forgot Password?</h2> */}
          <p style={{ margin: '0 0 1.5rem', fontSize: '0.85rem', color: '#6b7280' }}>Enter your registered email and we'll send a reset link.</p>

          {fpError && (
            <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', padding: '0.65rem 0.875rem', marginBottom: '1rem', fontSize: '0.82rem', color: '#dc2626' }}>{fpError}</div>
          )}

          <form onSubmit={handleForgotSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '0.4rem', fontWeight: 500, fontSize: '0.875rem', color: '#374151' }}>Email Address *</label>
              <input type="email" value={fpEmail} onChange={e => { setFpEmail(e.target.value); setFpError(''); }}
                placeholder="you@example.com" style={inputStyle(!!fpError)}
                onFocus={e => e.target.style.borderColor = '#16a34a'} onBlur={e => e.target.style.borderColor = '#d1d5db'} />
            </div>

            <button type="submit" disabled={fpLoading}
              style={{ width: '100%', padding: '0.7rem', background: fpLoading ? '#9ca3af' : '#16a34a', color: 'white', border: 'none', borderRadius: '8px', fontSize: '0.9rem', fontWeight: 600, cursor: fpLoading ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '7px' }}>
              {fpLoading ? <><ArrowPathIcon style={{ width: 17, height: 17, animation: 'spin 0.6s linear infinite' }} />Sending...</> : <><EnvelopeIcon style={{ width: 17, height: 17 }} />Send Reset Link</>}
            </button>

            <button type="button" onClick={() => { setScreen('login'); setFpError(''); setFpEmail(''); }}
              style={{ background: 'none', border: 'none', color: '#6b7280', cursor: 'pointer', fontSize: '0.85rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px', padding: 0 }}>
              <ArrowLeftIcon style={{ width: 14, height: 14 }} /> Back to Sign In
            </button>
          </form>
        </div>
      </div>
    );
  }

  // ── Forgot password — success + reset form ────────────────
  if (screen === 'forgot-success') {
    return (
      <div style={baseStyle} onClick={onClose}>
        <div style={cardStyle} onClick={e => e.stopPropagation()}>
          <style>{globalStyle}</style>
          <button onClick={onClose} style={{ position: 'absolute', top: '1rem', right: '1rem', background: 'none', border: 'none', fontSize: '1.4rem', cursor: 'pointer', color: '#9ca3af', width: 32, height: 32, borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            onMouseEnter={e => e.currentTarget.style.background = '#f3f4f6'} onMouseLeave={e => e.currentTarget.style.background = 'none'}>×</button>

          {rpSuccess ? (
            <div style={{ textAlign: 'center' }}>
              <div style={{ width: 52, height: 52, background: '#dcfce7', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1rem' }}>
                <CheckCircleIcon style={{ width: 26, height: 26, color: '#16a34a' }} />
              </div>
              <h2 style={{ margin: '0 0 0.3rem', fontSize: '1.15rem', fontWeight: 700, color: '#111827' }}>Password Reset!</h2>
              <p style={{ margin: '0 0 1.5rem', fontSize: '0.85rem', color: '#6b7280' }}>Your password has been updated. You can now sign in.</p>
              <button onClick={() => { setScreen('login'); setRpSuccess(false); setRpToken(''); setRpPassword(''); setRpConfirm(''); }}
                style={{ width: '100%', padding: '0.7rem', background: '#16a34a', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 600, fontSize: '0.9rem', cursor: 'pointer' }}>
                Sign In Now
              </button>
            </div>
          ) : (
            <>
              {/* Email sent notice */}
              <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '10px', padding: '0.875rem 1rem', marginBottom: '1.25rem', display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                <EnvelopeIcon style={{ width: 18, height: 18, color: '#16a34a', flexShrink: 0, marginTop: 2 }} />
                <div>
                  <p style={{ margin: 0, fontSize: '0.82rem', fontWeight: 600, color: '#15803d' }}>Reset link sent to {fpEmail}</p>
                  <p style={{ margin: '3px 0 0', fontSize: '0.78rem', color: '#374151' }}>Check your inbox (and spam). The link expires in 1 hour.</p>
                </div>
              </div>

              {/* Dev token notice */}
              {fpToken && (
                <div style={{ background: '#fefce8', border: '1px solid #fde047', borderRadius: '8px', padding: '0.75rem', marginBottom: '1rem', fontSize: '0.75rem', color: '#854d0e' }}>
                  <strong>Dev mode token:</strong>
                  <code style={{ display: 'block', marginTop: '4px', wordBreak: 'break-all', fontSize: '0.7rem', background: '#fff', padding: '4px 6px', borderRadius: '4px', border: '1px solid #fde047' }}>{fpToken}</code>
                </div>
              )}

              <h3 style={{ margin: '0 0 0.25rem', fontSize: '1rem', fontWeight: 700, color: '#111827' }}>Enter Reset Token & New Password</h3>
              <p style={{ margin: '0 0 1rem', fontSize: '0.8rem', color: '#6b7280' }}>Paste the token from your email below.</p>

              {rpError && (
                <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', padding: '0.65rem', marginBottom: '1rem', fontSize: '0.82rem', color: '#dc2626' }}>{rpError}</div>
              )}

              <form onSubmit={handleResetSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.35rem', fontWeight: 500, fontSize: '0.8rem', color: '#374151' }}>Reset Token *</label>
                  <input type="text" value={rpToken} onChange={e => { setRpToken(e.target.value); setRpError(''); }}
                    placeholder="Paste token from email" style={inputStyle(!!rpError)}
                    onFocus={e => e.target.style.borderColor = '#16a34a'} onBlur={e => e.target.style.borderColor = '#d1d5db'} />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.35rem', fontWeight: 500, fontSize: '0.8rem', color: '#374151' }}>New Password *</label>
                  <input type="password" value={rpPassword} onChange={e => { setRpPassword(e.target.value); setRpError(''); }}
                    placeholder="Min 8 characters" style={inputStyle(!!rpError)}
                    onFocus={e => e.target.style.borderColor = '#16a34a'} onBlur={e => e.target.style.borderColor = '#d1d5db'} />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.35rem', fontWeight: 500, fontSize: '0.8rem', color: '#374151' }}>Confirm Password *</label>
                  <input type="password" value={rpConfirm} onChange={e => { setRpConfirm(e.target.value); setRpError(''); }}
                    placeholder="Re-enter new password" style={inputStyle(!!rpError)}
                    onFocus={e => e.target.style.borderColor = '#16a34a'} onBlur={e => e.target.style.borderColor = '#d1d5db'} />
                </div>

                <button type="submit" disabled={rpLoading}
                  style={{ width: '100%', padding: '0.7rem', background: rpLoading ? '#9ca3af' : '#16a34a', color: 'white', border: 'none', borderRadius: '8px', fontSize: '0.9rem', fontWeight: 600, cursor: rpLoading ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '7px' }}>
                  {rpLoading ? <><ArrowPathIcon style={{ width: 17, height: 17, animation: 'spin 0.6s linear infinite' }} />Resetting...</> : <><LockClosedIcon style={{ width: 17, height: 17 }} />Reset Password</>}
                </button>

                <button type="button" onClick={() => setScreen('login')}
                  style={{ background: 'none', border: 'none', color: '#6b7280', cursor: 'pointer', fontSize: '0.82rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px', padding: 0 }}>
                  <ArrowLeftIcon style={{ width: 13, height: 13 }} /> Back to Sign In
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    );
  }

  // ── Login form ────────────────────────────────────────────
  return (
    <div style={baseStyle} onClick={onClose} className="login-modal-overlay">
      <div style={cardStyle} onClick={e => e.stopPropagation()} className="login-modal-card">
        <style>{globalStyle}</style>

        <button onClick={onClose} style={{ position: 'absolute', top: '1rem', right: '1rem', background: 'none', border: 'none', fontSize: '1.4rem', cursor: 'pointer', color: '#9ca3af', width: 32, height: 32, borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center' }} className="login-close-btn"
          onMouseEnter={e => e.currentTarget.style.background = '#f3f4f6'} onMouseLeave={e => e.currentTarget.style.background = 'none'}>×</button>

        <div style={{ marginBottom: '1.5rem' }}>
          <h2 style={{ margin: '0 0 0.25rem', color: '#111827', fontSize: '1.25rem', fontWeight: 700 }} className="login-title">Welcome Back</h2>
          <p style={{ margin: 0, color: '#6b7280', fontSize: '0.85rem' }} className="login-subtitle">Sign in to your ePayhub Store account</p>
        </div>

        {errors.general && (
          <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', padding: '0.65rem 0.875rem', marginBottom: '1rem', fontSize: '0.82rem', color: '#dc2626' }}>{errors.general}</div>
        )}

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '0.4rem', fontWeight: 500, fontSize: '0.875rem', color: '#374151' }} className="login-label">Email *</label>
            <input type="email" name="email" value={formData.email} onChange={handleInputChange}
              placeholder="you@example.com" style={{ ...inputStyle(errors.email), fontSize: '0.875rem' }} className="login-input"
              onFocus={e => e.target.style.borderColor = '#16a34a'} onBlur={e => e.target.style.borderColor = errors.email ? '#ef4444' : '#d1d5db'} />
            {errors.email && <p style={{ color: '#ef4444', fontSize: '0.75rem', margin: '4px 0 0' }}>{errors.email}</p>}
          </div>

          <div>
            <div style={{ marginBottom: '0.4rem' }}>
              <label style={{ fontWeight: 500, fontSize: '0.875rem', color: '#374151' }} className="login-label">Password *</label>
            </div>
            <div style={{ position: 'relative' }}>
              <input type={showPassword ? "text" : "password"} name="password" value={formData.password} onChange={handleInputChange}
                placeholder="Enter your password" style={{ ...inputStyle(errors.password), paddingRight: '2.5rem', fontSize: '0.875rem' }} className="login-input"
                onFocus={e => e.target.style.borderColor = '#16a34a'} onBlur={e => e.target.style.borderColor = errors.password ? '#ef4444' : '#d1d5db'} />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={{
                  position: 'absolute',
                  right: '0.75rem',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#6b7280',
                  padding: '4px',
                  borderRadius: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                onMouseEnter={e => e.currentTarget.style.color = '#374151'}
                onMouseLeave={e => e.currentTarget.style.color = '#6b7280'}
              >
                {showPassword ? <EyeSlashIcon style={{ width: 18, height: 18 }} /> : <EyeIcon style={{ width: 18, height: 18 }} />}
              </button>
              {errors.password && <p style={{ color: '#ef4444', fontSize: '0.75rem', margin: '4px 0 0' }}>{errors.password}</p>}
            </div>
          </div>

          <button type="submit" disabled={loading}
            style={{ width: '100%', padding: '0.7rem', background: loading ? '#9ca3af' : 'linear-gradient(135deg,#16a34a,#15803d)', color: 'white', border: 'none', borderRadius: '8px', fontSize: '0.95rem', fontWeight: 600, cursor: loading ? 'not-allowed' : 'pointer', marginTop: '4px', boxShadow: loading ? 'none' : '0 2px 8px rgba(22,163,74,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '7px' }} className="login-btn">
            {loading ? <><ArrowPathIcon style={{ width: 17, height: 17, animation: 'spin 0.6s linear infinite' }} />Signing In...</> : <><EnvelopeIcon style={{ width: 17, height: 17 }} />Sign In</>}
          </button>

          <p style={{ textAlign: 'center', margin: 0, fontSize: '0.85rem', color: '#6b7280' }}>
            Don't have an account?{' '}
            <button type="button" onClick={onSwitchToRegister}
              style={{ background: 'none', border: 'none', color: '#16a34a', cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem' }}>
              Register
            </button>
          </p>
        </form>
      </div>
    </div>
  );
}
